import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  InputAdornment,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Skeleton
} from '@mui/material';
import { Add, ExpandMore, DeleteOutline, PhotoCamera } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { preventNegativeInput, limitMaxTen } from 'src/utlis/form-validation';

const FloorPlansTab = ({
  form,
  addFloorPlan,
  updateFloorPlan,
  removeFloorPlan,
  handleFloorPlanImageUpload,
  loadingProperty,
  areaUnit,
  setField
}) => {
  return (
    <Box>
      {/* {console.log('floor paln ke andr area' + areaUnit)} */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 2, sm: 0 }}
        sx={{ mb: 3 }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'primary.titledark',
            fontFamily: 'poppinsMedium',
            fontWeight: 600
          }}
        >
          Floor Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addFloorPlan}
          disabled={loadingProperty}
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontFamily: 'poppinsMedium'
          }}
        >
          Add Floor Plan
        </Button>
      </Stack>

      {form.floorPlans.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            border: `2px dashed ${palette.grey[300]}`,
            backgroundColor: 'background.paper'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'grey.600',
              fontFamily: 'poppinsRegular'
            }}
          >
            {loadingProperty ? 'Loading floor plans...' : 'No floor plans added yet.'}
          </Typography>
        </Paper>
      )}

      {form.floorPlans.map((fp, index) => (
        <Accordion
          key={fp.id}
          defaultExpanded={index === 0}
          expanded={form.expandedFloorPlan === fp.id}
          onChange={(event, isExpanded) => {
            if (isExpanded) {
              setField('expandedFloorPlan', fp.id);
            } else {
              setField('expandedFloorPlan', null);
            }
          }}
          disabled={loadingProperty}
          sx={{
            mb: 2,
            borderRadius: 3,
            boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.08)',
            border: `1px solid ${palette.grey[200]}`,
            '&:before': { display: 'none' },
            '&.Mui-disabled': {
              backgroundColor: 'grey.50',
              opacity: 0.7
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: '#fff' }} />}
            sx={{
              backgroundColor: '#373537',
              borderBottom: `1px solid ${palette.grey[200]}`,
              borderRadius: '10px',
              minHeight: 68,
              '&.Mui-expanded': {
                minHeight: 68,
                borderBottom: `1px solid ${palette.grey[200]}`
              }
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <Typography
                sx={{
                  color: '#e7c873',
                  fontFamily: 'poppinsMedium',
                  fontSize: '1.1rem'
                }}
              >
                {fp.title || `Floor Plan`}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFloorPlan(fp.id);
                }}
                disabled={loadingProperty}
                sx={{
                  backgroundColor: 'red'
                }}
              >
                <DeleteOutline sx={{ color: 'white' }} />
              </IconButton>
            </Stack>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              p: 3,
              backgroundColor: 'background.paper'
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  value={fp.title || ''}
                  onChange={(e) => updateFloorPlan(fp.id, 'title', e.target.value)}
                  fullWidth
                  disabled={loadingProperty}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  label="Bedrooms"
                  type="number"
                  value={fp.bedrooms}
                  onChange={(e) => updateFloorPlan(fp.id, 'bedrooms', e.target.value)}
                  fullWidth
                  disabled={loadingProperty}
                  onKeyDown={preventNegativeInput}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  label="Bathrooms"
                  type="number"
                  value={fp.bathrooms}
                  onChange={(e) => updateFloorPlan(fp.id, 'bathrooms', e.target.value)}
                  fullWidth
                  disabled={loadingProperty}
                  onKeyDown={preventNegativeInput}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  label="Floor Price"
                  type="number"
                  value={fp.floorPrice}
                  onChange={(e) => updateFloorPlan(fp.id, 'floorPrice', e.target.value)}
                  fullWidth
                  disabled={loadingProperty}
                  onKeyDown={preventNegativeInput}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Floor Size"
                  type="number"
                  value={fp.floorSize}
                  onChange={(e) => updateFloorPlan(fp.id, 'floorSize', e.target.value)}
                  fullWidth
                  disabled={loadingProperty}
                  onKeyDown={preventNegativeInput}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Area Unit"
                  value={areaUnit || ''}
                  fullWidth
                  InputProps={{
                    readOnly: true
                  }}
                  sx={{
                    backgroundColor: 'grey.100',
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      fontFamily: 'poppinsMedium',
                      fontWeight: 600,
                      color: 'primary.title'
                    }}
                  >
                    Floor Plan Image
                  </Typography>

                  <input
                    accept="image/*"
                    id={`floorplan-image-${fp.id}`}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFloorPlanImageUpload(file, fp.id)
                          .then((response) => {})
                          .catch((error) => {});
                      }
                      e.target.value = null;
                    }}
                    disabled={loadingProperty}
                  />

                  {!fp.image ? (
                    <label htmlFor={`floorplan-image-${fp.id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        startIcon={<PhotoCamera />}
                        sx={{
                          height: 64,
                          border: `2px dashed ${palette.grey[400]}`,
                          backgroundColor: 'grey.50',
                          color: 'grey.700',
                          textTransform: 'none',
                          fontFamily: 'poppinsMedium',
                          fontSize: '1rem',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.lighter',
                            color: 'primary.dark'
                          },
                          '&:disabled': {
                            borderColor: 'grey.300',
                            backgroundColor: 'grey.100',
                            color: 'grey.500'
                          },
                          transition: 'all 0.3s ease'
                        }}
                        disabled={loadingProperty}
                      >
                        Upload Floor Plan Image
                      </Button>
                    </label>
                  ) : (
                    <Card
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        border: `1px solid ${palette.grey[200]}`,
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <a
                          href={fp.image.preview || fp.image.serverUrl}
                          data-fancybox="floorplan-gallery"
                          data-caption={fp.title || 'Floor Plan Image'}
                        >
                          <Box
                            component="img"
                            src={fp.image.preview || fp.image.serverUrl}
                            alt={fp.image.name}
                            sx={{
                              width: '100%',
                              height: 240,
                              objectFit: 'contain',
                              borderRadius: 2,
                              backgroundColor: 'grey.50',
                              opacity: fp.image.isUploading || fp.image.uploadError ? 0.5 : 1,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'scale(1.02)'
                              }
                            }}
                          />
                        </a>
                        {fp.image.isUploading && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)'
                            }}
                          >
                            <CircularProgress size={48} sx={{ color: 'primary.main' }} />
                          </Box>
                        )}
                        {fp.image.uploadError && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              backgroundColor: 'error.main',
                              color: 'white',
                              px: 2,
                              py: 1,
                              fontSize: '12px',
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            Upload Failed
                          </Box>
                        )}
                      </Box>
                      <CardContent sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography
                            variant="body1"
                            noWrap
                            sx={{
                              maxWidth: '70%',
                              fontFamily: 'poppinsRegular',
                              color: 'text.primary'
                            }}
                          >
                            {fp.image.name}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Change Image">
                              <label htmlFor={`floorplan-image-${fp.id}`}>
                                <IconButton
                                  size="medium"
                                  component="span"
                                  sx={{
                                    color: 'primary.main',
                                    backgroundColor: 'primary.lighter',
                                    '&:hover': {
                                      backgroundColor: 'primary.hover',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                  disabled={loadingProperty || fp.image.isUploading}
                                >
                                  <PhotoCamera />
                                </IconButton>
                              </label>
                            </Tooltip>
                            <Tooltip title="Remove Image">
                              <IconButton
                                size="medium"
                                onClick={() => updateFloorPlan(fp.id, 'image', null)}
                                sx={{
                                  color: 'error.main',
                                  backgroundColor: 'error.lighter',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                                disabled={loadingProperty || fp.image.isUploading}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={fp.notes}
                  onChange={(e) => updateFloorPlan(fp.id, 'notes', e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={loadingProperty}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default FloorPlansTab;
